import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDiscourseWebhook } from '../hooks/useDiscourseWebhook';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LoginPage: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading, login } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [loginLoading, setLoginLoading] = useState<boolean>(false);
  const [showFallback, setShowFallback] = useState<boolean>(false);
  const { registerPopupSession, startPolling } = useDiscourseWebhook();

  // Show loading screen while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-command-dark-blue flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to home if already authenticated (after loading is complete)
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const handleLogin = async (): Promise<void> => {
    try {
      setLoginLoading(true);
      setError(null);

      // Register popup session for webhook tracking
      // We'll register without specific user info and match any login in the webhook
      console.log('🔐 Registering popup session for webhook tracking');
      const webhookSessionId = await registerPopupSession('*', '*');
      
      // Get SSO URL
      const response = await fetch(`${BACKEND_URL}/api/auth/popup-sso-url`);
      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to get SSO URL');
      }

      // Open popup
      const popup = window.open(
        data.sso_url,
        'discourse-sso',
        'width=700,height=800,scrollbars=yes,resizable=yes'
      );

      if (!popup) {
        // Popup blocked - show fallback
        setShowFallback(true);
        setLoginLoading(false);
        setError('Popup was blocked. Please use the alternative login method below.');
        return;
      }

      // Monitor if we should close forum popup due to webhook
      const checkForumClose = setInterval(async () => {
        try {
          if (popup && !popup.closed) {
            const response = await fetch(`${BACKEND_URL}/api/webhooks/should-close-popup`);
            
            if (response.headers.get('content-type')?.includes('text/html')) {
              // Server says popup should close
              console.log('🚪 Webhook detected, closing forum popup');
              popup.close();
              clearInterval(checkForumClose);
            }
          } else {
            clearInterval(checkForumClose);
          }
        } catch (error) {
          // Ignore fetch errors
        }
      }, 2000); // Check every 2 seconds

      console.log('🎯 Popup opened, starting webhook polling');
      
      // Start polling for webhook authentication
      startPolling(webhookSessionId, (userData) => {
        console.log('✅ User authenticated via webhook!', userData);
        
        // Close the popup aggressively
        console.log('🚪 Attempting to close popup window');
        let popupClosed = false;
        
        const closeAttempts = [100, 500, 1000, 2000];
        closeAttempts.forEach(delay => {
          setTimeout(() => {
            if (popup && !popup.closed && !popupClosed) {
              try {
                popup.close();
                popupClosed = true;
                console.log('✅ Popup closed successfully');
              } catch (error) {
                console.log('⚠️ Popup close attempt failed:', error);
              }
            }
          }, delay);
        });
        
        // Get session ID and trigger auth success event
        fetch(`${BACKEND_URL}/api/webhooks/check-popup/${webhookSessionId}`)
          .then(res => res.json())
          .then(sessionData => {
            window.dispatchEvent(new CustomEvent('auth:success', {
              detail: { 
                user: userData,
                sessionId: sessionData.sessionId 
              }
            }));
          })
          .catch(() => {
            // Fallback: just use user data
            window.dispatchEvent(new CustomEvent('auth:success', {
              detail: { user: userData }
            }));
          });
        
        setLoginLoading(false);
        setError(null);
        
        // Clear forum close monitoring when auth succeeds
        clearInterval(checkForumClose);
      });

      // Monitor popup status
      const checkPopup = setInterval(() => {
        try {
          if (popup.closed) {
            clearInterval(checkPopup);
            setLoginLoading(false);
            return;
          }
        } catch (e) {
          console.error('Error monitoring popup:', e);
        }
      }, 2000);

      // Timeout after 2 minutes
      setTimeout(() => {
        clearInterval(checkPopup);
        clearInterval(checkForumClose);
        if (popup && !popup.closed) {
          popup.close();
        }
        setLoginLoading(false);
        if (!isAuthenticated) {
          setError('Login timeout. Please try again.');
        }
      }, 2 * 60 * 1000);

    } catch (err) {
      console.error('Error during popup login:', err);
      setError(err instanceof Error ? err.message : 'Login failed');
      setLoginLoading(false);
    }
  };

  const handleFallbackLogin = async (): Promise<void> => {
    try {
      setLoginLoading(true);
      setError(null);

      // Get SSO URL for redirect
      const response = await fetch(`${BACKEND_URL}/api/auth/redirect-sso-url`);
      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to get SSO URL');
      }

      // Redirect to SSO (full page redirect)
      window.location.href = data.sso_url;
      
    } catch (err) {
      console.error('Error during fallback login:', err);
      setError(err instanceof Error ? err.message : 'Login failed');
      setLoginLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-command-dark-blue flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Command Legacy</h1>
          <p className="text-gray-600">Sign in to continue</p>
        </div>
        
        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

<button
  onClick={handleLogin}
  disabled={loginLoading}
  className={`w-full flex items-center justify-center px-4 py-3 border border-transparent text-base font-medium rounded-md text-white transition-colors duration-200 ${
    loginLoading
      ? 'bg-gray-400 cursor-not-allowed'
      : 'bg-command-dark-blue bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 ring-blue-500'
  }`}
>
  {loginLoading ? (
    <>
      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
      Signing in...
    </>
  ) : (
    <>
      <img 
        src="/command-legacy.jpg" 
        alt="Command Legacy" 
        className="w-8 h-6 mr-3 object-contain"
      />
      Sign In
    </>
  )}
</button>
        {/* Fallback Login Button */}
        {showFallback && (
          <div className="mt-4">
            <div className="text-center mb-3">
              <p className="text-sm text-gray-600">Having trouble with popups?</p>
            </div>
            <button
              onClick={handleFallbackLogin}
              disabled={loginLoading}
              className="w-full px-4 py-2 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
            >
              Use Alternative Login
            </button>
          </div>
        )}

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Secure authentication powered by Discourse
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;